import { useTheme } from '@emotion/react';
import { CircularProgress, alpha } from '@mui/material';
import Box from '@mui/material/Box';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import { DateTime } from 'luxon';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';

import InsetDrawer from '@/components/drawer/InsetDrawer';
import CloudRoundedIcon from '@mui/icons-material/CloudRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';

import { useTranslation } from 'react-i18next';

import HighchartsMore from 'highcharts/highcharts-more';
HighchartsMore(Highcharts); // Must load highcharts-more for arearange

import CardHeaderWithIcon from '@/components/card/CardHeaderWithIcon';
import { formatNumber } from '@/utils/formatters';
import { Card, CardContent, Chip, Grid, Typography } from '@mui/material';

const toUTC = (timestamp) => {
  if (!timestamp) return null;
  const dt = DateTime.fromISO(timestamp).toUTC();
  return dt.isValid ? dt.toMillis() : null;
};

function createArearangeData(
  predicted = [],
  comparison = [],
  useAdvancedModel,
  averageValue,
) {
  const merged = [];

  if (useAdvancedModel) {
    if (
      !Array.isArray(predicted) ||
      !Array.isArray(comparison) ||
      !predicted.length ||
      !comparison.length
    ) {
      return [];
    }

    // Use predicted values normally
    for (let i = 0; i < predicted.length; i++) {
      const [xP, yP] = predicted[i];
      const [xC, yC] = comparison[i];

      if (xP !== xC) continue; // Ensure alignment

      merged.push([xP, yC, yP]); // Lower = comparison, Upper = predicted
    }
  } else {
    // Return a simple horizontal line at the averageValue
    if (!comparison.length) return []; // Ensure there is data

    const startX = comparison[0][0]; // First timestamp
    const endX = comparison[comparison.length - 1][0]; // Last timestamp

    return [
      [startX, averageValue],
      [endX, averageValue],
    ];
  }

  return merged;
}

const CustomLegend = ({
  chartRef,
  averages,
  handleToggleSeries,
  seriesVisibility,
}) => {
  if (
    !chartRef.current?.chart.series ||
    !seriesVisibility.length ||
    !averages.length
  ) {
    return null; // Prevent rendering if data is not ready
  }

  return (
    <Grid container spacing={2}>
      {chartRef.current.chart.series.map((series) => {
        const seriesAverages = averages.find(
          (avg) => avg.name === series.name,
        )?.averages;
        const isVisible = seriesVisibility.find(
          (s) => s.name === series.name,
        )?.visible;

        if (!series.options.isInternal && series.options.id) {
          return (
            <Grid item key={series.options.id || series.name} xs={6} lg={3}>
              <Card
                onClick={() => handleToggleSeries(series)}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  cursor: 'pointer',
                  height: '100%',
                  opacity: isVisible ? 1 : 0.3, // Dim if hidden
                  boxShadow: '0 20px 27px 0 rgb(0 0 0 / 5%)',
                  ':hover': {
                    boxShadow: '0 20px 27px 0 rgb(0 0 0 / 15%)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => {
                  chartRef.current.chart.series.forEach((s) => {
                    if (s !== series) {
                      s.group.element.style.transition = 'opacity 0.3s ease';
                      s.group.element.style.opacity = 0.2;
                    }
                  });
                }}
                onMouseLeave={() => {
                  chartRef.current.chart.series.forEach((s) => {
                    s.group.element.style.transition = 'opacity 0.3s ease';
                    s.group.element.style.opacity = 1;
                  });
                }}
              >
                <CardContent>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={series.color}
                      >
                        {series.name}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        }
      })}
    </Grid>
  );
};

const InitiativeChartDrawer = ({ open, onClose, selectedInitiative }) => {
  const chartComponentRef = useRef(null);
  const [isChartReady, setIsChartReady] = useState(false);
  const [averages, setAverages] = useState([]);
  const [seriesVisibility, setSeriesVisibility] = useState([]);
  const [mode, setMode] = useState(
    selectedInitiative?.advancedSavingsAvailable ? 'advanced' : 'simple',
  );
  const { t } = useTranslation('energyxplorer-initiatives-tab');

  useEffect(() => {
    const advancedAvailable = !!selectedInitiative?.advancedSavingsAvailable;
    setMode(advancedAvailable ? 'advanced' : 'simple');
  }, [selectedInitiative?.advancedSavingsAvailable]);

  const theme = useTheme();

  const processPredictedEnergyUsage = useCallback((predictedData) => {
    if (!Array.isArray(predictedData)) return [];
    return predictedData.map((entry) => [
      toUTC(entry.timestamp),
      entry.value, // Already in kWh, no conversion needed
    ]);
  }, []);

  const processBaselineEnergyEnergyUsage = useCallback((baselineData) => {
    if (!Array.isArray(baselineData)) return [];
    return baselineData.map((entry) => [
      toUTC(entry.timestamp),
      entry.value, // Already in kWh, no conversion needed
    ]);
  }, []);

  const processComparisonEnergyUsage = useCallback((comparisonData) => {
    if (!Array.isArray(comparisonData)) return [];
    return comparisonData.map((entry) => [
      toUTC(entry.timestamp),
      entry.value, // Already in kWh, no conversion needed
    ]);
  }, []);

  const processInitiativeEnergyUsage = useCallback((initiativeData) => {
    if (!Array.isArray(initiativeData)) return [];
    return initiativeData.map((entry) => [
      toUTC(entry.timestamp),
      entry.value, // Already in kWh, no conversion needed
    ]);
  }, []);

  const energyData =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings;

  const generateSeries = useCallback(() => {
    if (!selectedInitiative) return [];

    const baselineAvg =
      energyData?.EnergySavingsSummary?.AverageBaselineEnergyUsage;
    const comparisonAvg =
      energyData?.EnergySavingsSummary?.AverageComparisonEnergyUsage;

    const baselineStart = toUTC(selectedInitiative?.baselineStart);
    const baselineEnd = toUTC(selectedInitiative?.baselineEnd);
    const reportingStart = toUTC(selectedInitiative?.comparisonStart);
    const reportingEnd = toUTC(selectedInitiative?.comparisonEnd);

    const stepLineData = [
      [baselineStart, baselineAvg],
      [baselineEnd, baselineAvg],
      [reportingStart, comparisonAvg],
      [reportingEnd, comparisonAvg],
    ];

    // Add the predicted energy series conditionally
    const predictedEnergySeries = energyData?.PredictedEnergyUsage
      ? [
          {
            id: 'baseline-energy-usage',
            name: t('chart.baseline_usage'),
            data: processBaselineEnergyEnergyUsage(
              energyData.BaselineEnergyUsage,
            ),
            type: 'line',
            color: '#42a5f5',
            dataGrouping: { enabled: false },
          },
          {
            id: 'initiative-energy-usage',
            name: t('chart.implementation_usage'),
            data: processInitiativeEnergyUsage(
              energyData?.InitiativeEnergyUsage,
            ),
            type: 'line',
            color: '#0b6477',
          },
          {
            id: 'comparison-energy-usage',
            name: t('chart.post_implementation_usage'),
            zIndex: 1,
            data: processComparisonEnergyUsage(
              energyData.ComparisonEnergyUsage,
            ),
            type: 'line',
            color: '#42a5f5',
            dataGrouping: { enabled: false },
          },
          {
            id: 'predicted-vs-comparison-band',
            type: mode === 'advanced' ? 'arearange' : 'line',
            name:
              mode === 'advanced'
                ? t('chart.projected_usage_without_initiative')
                : t('chart.average'),
            data: createArearangeData(
              processPredictedEnergyUsage(energyData?.PredictedEnergyUsage),
              processComparisonEnergyUsage(energyData?.ComparisonEnergyUsage),
              mode === 'advanced',
              baselineAvg,
            ),
            color: mode === 'advanced' ? '#73EC8B' : '#15B392',
            dashStyle: mode === 'advanced' ? undefined : 'Dash',
            fillOpacity: mode === 'advanced' ? 0.3 : 0,
            lineWidth: mode === 'advanced' ? 2 : 4,
            dataGrouping: { enabled: false },
            zIndex: mode === 'advanced' ? 0 : 5,
          },
          {
            name: t('chart.average_usage_baseline'),
            type: 'line',
            step: 'left',
            color: 'red',
            data: stepLineData || [],
            zIndex: 5,
          },
        ]
      : [];

    return [...predictedEnergySeries];
  }, [
    selectedInitiative,
    energyData,
    mode,
    t,
    processBaselineEnergyEnergyUsage,
    processInitiativeEnergyUsage,
    processComparisonEnergyUsage,
    processPredictedEnergyUsage,
  ]);

  const [chartOptions, setChartOptions] = useState({
    credits: { enabled: false },
    chart: {
      height: 600,
      zooming: { type: 'x', mouseWheel: { enabled: false } },
      navigator: { enabled: false },
      scrollbar: { enabled: false },
      title: { text: 'Power Consumption Data' },
      events: {
        load: function () {
          setIsChartReady(true);
        },
        destroy: function () {
          setIsChartReady(false);
        },
      },
    },
    time: { useUTC: true },
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    },
    rangeSelector: { enabled: false },
    xAxis: {
      type: 'datetime',
      plotLines: [], // <-- empty initially
      plotBands: [],
      gridLineWidth: 1,
      dateTimeLabelFormats: {
        millisecond: '%H:%M:%S.%L',
        second: '%H:%M:%S',
        minute: '%H:%M',
        hour: '%H:%M',
        day: '%a %e. %b', // e.g. "man 6. jan"
        week: '%e. %b',
        month: '%b %Y',
        year: '%Y',
      },
    },
    yAxis: {
      title: {
        text: `${t('chart.energy_usage')} (kWh)`,
      },
      gridLineWidth: 1,
      opposite: false, // Show on the right side
    },
    tooltip: {
      shared: true,
      valueSuffix: ' kWh',
      valueDecimals: 2, // Display up to 2 decimal places
    },
    scrollbar: { enabled: false },
    loading: {
      labelStyle: {
        fontWeight: 'bold',
        fontSize: '16px',
        color: '#666',
      },
      style: {
        backgroundColor: 'transparent',
        opacity: 0,
      },
      showDuration: 100, // Smoothly show
      hideDuration: 100, // Smoothly hide
    },
    series: [],
    plotOptions: {
      arearange: {
        gapSize: 0, // or explicitly gapDetection: false in newer versions
      },
      line: {
        gapSize: 0, // or explicitly gapDetection: false in newer versions
      },
    },
  });

  useEffect(() => {
    if (!selectedInitiative) {
      setAverages([]);
      setSeriesVisibility([]);
      setChartOptions((prev) => ({
        ...prev,
        xAxis: {
          ...prev.xAxis,
          plotLines: [],
          plotBands: [],
        },
        yAxis: {
          ...prev.yAxis,
        },
      }));
      return;
    }

    const series = generateSeries();

    setSeriesVisibility(series.map((s) => ({ name: s.name, visible: true })));
    const baselineEndMs = toUTC(selectedInitiative.baselineEnd);
    const reportingStartMs = toUTC(selectedInitiative.comparisonStart);

    setChartOptions((prev) => ({
      ...prev,
      series: [...series],
      navigator: {
        enabled: false, // Keep disabled
      },
      xAxis: {
        ...prev.xAxis,
        plotLines: baselineEndMs
          ? [
              {
                value: baselineEndMs,
                color: '	#0b6477',
                dashStyle: 'Dash',
                width: 2,
                zIndex: 5,
                label: {
                  text: selectedInitiative?.name || selectedInitiative?.title,
                  style: { color: '		#0b6477' },
                },
              },
            ]
          : [],
        plotBands:
          baselineEndMs != null && reportingStartMs != null
            ? [
                {
                  color: '	#0b63773a',
                  from: baselineEndMs,
                  to: reportingStartMs,
                  label: { text: '', style: { color: '	#0b6477' } },
                },
              ]
            : [],
      },
      yAxis: {
        ...prev.yAxis,
      },
      loading: {
        labelStyle: {
          fontWeight: 'bold',
          fontSize: '16px',
          color: '#666',
        },
        style: {
          backgroundColor: 'transparent',
          opacity: 0,
        },
        showDuration: 100, // Smoothly show
        hideDuration: 100, // Smoothly hide
      },
    }));
  }, [selectedInitiative, energyData, mode, generateSeries]);

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;
    Highcharts.setOptions(themeOptions);

    if (
      isChartReady &&
      chartComponentRef.current &&
      chartComponentRef.current.chart
    ) {
      chartComponentRef.current.chart.update({
        chart: {
          backgroundColor: themeOptions.chart.backgroundColor,
        },
        title: {
          style: {
            color: themeOptions.title.style.color,
          },
        },
        xAxis: {
          ...chartOptions.xAxis,
          gridLineColor: themeOptions.xAxis.gridLineColor,
          lineColor: themeOptions.xAxis.lineColor,
          labels: {
            style: {
              color: themeOptions.xAxis.labels.style.color,
            },
          },
        },
        yAxis: [
          {
            gridLineColor: themeOptions.yAxis.gridLineColor,
            labels: {
              style: {
                color: themeOptions.yAxis.labels.style.color,
              },
            },
          },
          {
            gridLineColor: themeOptions.yAxis.gridLineColor,
            labels: {
              style: {
                color: themeOptions.yAxis.labels.style.color,
              },
            },
          },
        ],
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          borderColor: themeOptions.tooltip.borderColor,
        },
      });
    }
  }, [chartOptions.xAxis, isChartReady, theme]);

  useEffect(() => {
    if (chartComponentRef.current?.chart.series) {
      const updatedSeriesVisibility = chartComponentRef.current.chart.series
        .filter((series) => !series.options.isInternal) // Exclude navigator or internal series
        .map((series) => ({
          name: series.name,
          visible: series.visible,
        }));

      setSeriesVisibility(updatedSeriesVisibility);
    }
  }, [chartComponentRef.current?.chart.series]);

  const handleToggleSeries = (series) => {
    const newVisibility = !series.visible;
    series.setVisible(newVisibility); // Update visibility in Highcharts

    // Update local visibility state
    setSeriesVisibility((prev) =>
      prev.map((s) =>
        s.name === series.name ? { ...s, visible: newVisibility } : s,
      ),
    );
  };

  const energySavings =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
          ?.EnergySavingsSummary?.EnergySavings
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings
          ?.EnergySavingsSummary?.EnergySavings;

  const energySavingsPercentage =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
          ?.EnergySavingsSummary?.EnergySavingsPercentage
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings
          ?.EnergySavingsSummary?.EnergySavingsPercentage;

  const co2eSavings =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
          ?.CO2eSavingsSummary?.CO2eSavings
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings
          ?.CO2eSavingsSummary?.CO2eSavings;

  const co2eSavingsPercentage =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
          ?.CO2eSavingsSummary?.CO2eSavingsPercentage
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings
          ?.CO2eSavingsSummary?.CO2eSavingsPercentage;

  const getTrendColor = (percentage) => {
    if (percentage >= 0) {
      return 'success'; // Green for positive savings
    } else {
      return 'error'; // Red for negative savings
    }
  };

  return (
    <InsetDrawer
      open={open}
      onClose={onClose}
      width={1600}
      anchor="right"
      title={
        selectedInitiative?.name ||
        selectedInitiative?.main?.name ||
        'Initiativ'
      }
      content={
        <>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {selectedInitiative?.description ||
              selectedInitiative?.main?.description}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          ></Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs>
              <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                <CardHeaderWithIcon
                  icon={<EnergySavingsLeafRoundedIcon />}
                  title={
                    selectedInitiative?.state === 'Archived'
                      ? t('chart.energy_savings')
                      : 'Nuværende energibesparelse'
                  }
                  tooltip={t('chart.energy_savings_description')}
                />
                <Typography
                  variant="h4"
                  color="textPrimary"
                  fontWeight="bold"
                  gutterBottom
                >
                  {energySavings
                    ? `${formatNumber(energySavings)} kWh`
                    : '0 kWh'}
                  <Chip
                    variant="filled"
                    sx={{
                      marginLeft: 1,
                      mb: 1,
                      borderRadius: 2,
                      backgroundColor: (theme) =>
                        alpha(
                          theme.palette[getTrendColor(energySavingsPercentage)]
                            .main,
                          0.1,
                        ),
                      color: (theme) =>
                        theme.palette.mode === 'light'
                          ? theme.palette[
                              getTrendColor(energySavingsPercentage)
                            ].dark
                          : theme.palette[
                              getTrendColor(energySavingsPercentage)
                            ].light,
                    }}
                    label={
                      energySavingsPercentage
                        ? `${energySavingsPercentage.toFixed(1)}%`
                        : '0%'
                    }
                    size="small"
                  />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('chart.energy_savings_for')}{' '}
                  {selectedInitiative?.name || 'valgt initiativ'}
                </Typography>
              </Card>
            </Grid>
            {selectedInitiative?.state === 'Archived' && (
              <Grid item xs>
                <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                  <CardHeaderWithIcon
                    icon={<CloudRoundedIcon />}
                    title={t('chart.co2e_reduction')}
                    tooltip={t('chart.co2e_reduction_description')}
                  />
                  <Typography
                    variant="h4"
                    color="textPrimary"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {co2eSavings
                      ? `${formatNumber(co2eSavings / 1000)} kg CO2e`
                      : '0 kg CO2e'}
                    <Chip
                      variant="filled"
                      sx={{
                        marginLeft: 1,
                        mb: 1,
                        borderRadius: 2,
                        backgroundColor: (theme) =>
                          alpha(
                            theme.palette[getTrendColor(co2eSavingsPercentage)]
                              .main,
                            0.1,
                          ),
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? theme.palette[
                                getTrendColor(co2eSavingsPercentage)
                              ].dark
                            : theme.palette[
                                getTrendColor(co2eSavingsPercentage)
                              ].light,
                      }}
                      label={
                        co2eSavingsPercentage
                          ? `${co2eSavingsPercentage.toFixed(1)}%`
                          : '0%'
                      }
                      size="small"
                    />
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('chart.co2e_reduction_for')}{' '}
                    {selectedInitiative?.name || 'valgt initiativ'}
                  </Typography>
                </Card>
              </Grid>
            )}
            <Grid item xs>
              <Card
                variant="outlined"
                display="flex"
                flexDirection="row"
                onClick={() => {
                  if (!selectedInitiative?.advancedSavingsAvailable) {
                    return;
                  } else {
                    setMode(mode === 'advanced' ? 'simple' : 'advanced');
                  }
                }}
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light' ? '#f5f5f5' : '#303030',
                  padding: 2,
                  height: '100%',
                  cursor:
                    selectedInitiative?.advancedSavingsAvailable && 'pointer',
                  color: (theme) => theme.palette.text.secondary,
                  '&:hover': selectedInitiative?.advancedSavingsAvailable && {
                    backgroundColor: (theme) => theme.palette.action.hover,
                    color: (theme) => theme.palette.text.primary,
                    boxShadow: (theme) => theme.shadows[2],
                  },
                  transition:
                    'background-color 0.3s, color 0.3s, box-shadow 0.3s',
                }}
              >
                <CardHeaderWithIcon
                  icon={<ModelTrainingRoundedIcon />}
                  title={
                    mode === 'advanced'
                      ? t('cards.advanced_model_used.title')
                      : t('cards.simple_model_used.title')
                  }
                >
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    {mode === 'advanced'
                      ? t('cards.advanced_model_used.description')
                      : t('cards.simple_model_used.description')}
                  </Typography>
                </CardHeaderWithIcon>
              </Card>
            </Grid>
          </Grid>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '450px',
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
              <CardContent>
                <HighchartsReact
                  highcharts={Highcharts}
                  constructorType={'stockChart'}
                  options={chartOptions}
                  ref={chartComponentRef}
                />
              </CardContent>
            </Card>
          </Suspense>
          <CustomLegend
            chartRef={chartComponentRef}
            averages={averages}
            handleToggleSeries={handleToggleSeries}
            seriesVisibility={seriesVisibility}
          />
        </>
      }
    />
  );
};

export default InitiativeChartDrawer;
